import { useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BackButton, Badge, Button, Card, CardContent, CardHeader, CardTitle, ConfirmDialog, Input, PageHeader, PhoneInput, SearchSelect, Switch, TimeField } from '@doccareplus/ui';
import { Activity, Bell, Building2, CalendarClock, ChevronDown, ChevronRight, ChevronUp, ShieldCheck, Stethoscope } from 'lucide-react';
import { useTranslation } from '@doccareplus/shared/lib';
import { COUNTRIES, dialCodeFor, nationalPhoneLengthLimit, toE164 } from '@doccareplus/shared/lib/phone';
import { showToast, toastApiError } from '@doccareplus/kernel/application';
import { useTimeFormat } from '@doccareplus/kernel';
import { cn } from '@doccareplus/shared/lib/utils';
import { LocationSelect, type LocationValue } from '@/shared/ui/LocationSelect';
import { CURRENCIES, TIMEZONES, withValue } from '@/shared/geo/options';
import type {
  Availability, ClinicProfile, ClinicSettings, IntakeFieldKey, IntakeFormResponse, RoomItem, ServiceItem,
  UpdateClinicProfileRequest,
} from '../../infrastructure/settings.api';
import { useIsMobileSurface } from './useIsMobileSurface';

/** Best-effort split of a stored E.164 phone into (country, national) for editing. */
function splitE164(phone: string | null | undefined, fallbackCountry: string): { country: string; national: string } {
  const digits = (phone ?? '').replace(/\D/g, '');
  if (!digits) return { country: fallbackCountry, national: '' };
  const fallbackDial = dialCodeFor(fallbackCountry);
  if (fallbackDial && digits.startsWith(fallbackDial)) {
    return { country: fallbackCountry, national: digits.slice(fallbackDial.length) };
  }
  const match = [...COUNTRIES].sort((a, b) => b.dialCode.length - a.dialCode.length)
    .find((c) => digits.startsWith(c.dialCode));
  return match ? { country: match.iso2, national: digits.slice(match.dialCode.length) } : { country: fallbackCountry, national: digits };
}

export type ClinicSection = 'profile' | 'services' | 'availability' | 'intake' | 'reminders' | 'security';

// Sub-nav is grouped into plain-language buckets so clinic staff can scan it like the design's
// left rail. Only sections the current role can actually reach are rendered (see `visibleGroups`).
const sectionGroups: { groupKey: string; sections: ClinicSection[] }[] = [
  { groupKey: 'groups.clinic', sections: ['profile', 'services', 'intake'] },
  { groupKey: 'groups.scheduling', sections: ['availability'] },
  { groupKey: 'groups.communication', sections: ['reminders'] },
  { groupKey: 'groups.system', sections: ['security'] },
];

interface Props {
  sections: ClinicSection[];
  profile?: ClinicProfile;
  clinicSettings?: ClinicSettings;
  intakeForm?: IntakeFormResponse;
  services: ServiceItem[];
  rooms: RoomItem[];
  availability?: Availability;
  canManageServices: boolean;
  canManageRooms: boolean;
  canManageAvailability: boolean;
  isSaving: boolean;
  onSaveProfile: (value: UpdateClinicProfileRequest) => Promise<unknown>;
  onSaveClinicSettings: (value: ClinicSettings) => Promise<unknown>;
  onAddService: (value: Omit<ServiceItem, 'id' | 'isActive'>) => Promise<unknown>;
  onUpdateService: (value: ServiceItem) => Promise<unknown>;
  onAddRoom: (name: string) => Promise<unknown>;
  onUpdateRoom: (value: RoomItem) => Promise<unknown>;
  onSaveAvailability: (value: Pick<Availability, 'clinicDays' | 'providers' | 'holidays'>) => Promise<unknown>;
  onCreateLeave: (value: { startDate: string; endDate: string; startTime?: string | null; endTime?: string | null; reason: string }) => Promise<unknown>;
  onDecideLeave: (value: { leaveId: string; decision: 'Approved' | 'Rejected' }) => Promise<unknown>;
  onCancelLeave: (leaveId: string) => Promise<unknown>;
}

const icons: Record<ClinicSection, ReactNode> = {
  profile: <Building2 />, services: <Stethoscope />, availability: <CalendarClock />,
  intake: <Activity />, reminders: <Bell />, security: <ShieldCheck />,
};

export function ClinicSettingsWorkspace(props: Props) {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const isMobile = useIsMobileSurface();
  // The active tab lives in the URL (?tab=security) so a section can be deep-linked.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as ClinicSection | null;
  const activeSection: ClinicSection | null =
    tabParam && props.sections.includes(tabParam) ? tabParam : isMobile ? null : props.sections[0] ?? null;
  const setSection = (next: ClinicSection | null) => {
    setSearchParams((params) => {
      if (next) params.set('tab', next); else params.delete('tab');
      return params;
    }, { replace: true });
  };

  const labels: Record<ClinicSection, string> = {
    profile: t('tabs.profile'), services: t('tabs.services'),
    availability: t('tabs.availability'), reminders: t('tabs.reminders'),
    intake: t('tabs.intake'), security: t('tabs.security'),
  };
  const descriptions: Record<ClinicSection, string> = {
    profile: t('sectionDesc.profile'), services: t('sectionDesc.services'),
    availability: t('sectionDesc.availability'),
    intake: t('sectionDesc.intake'),
    reminders: t('sectionDesc.reminders'), security: t('sectionDesc.security'),
  };

  const body = activeSection === 'profile' && props.profile ? <ProfileSection profile={props.profile} saving={props.isSaving} onSave={props.onSaveProfile} />
    : activeSection === 'services' ? <ServicesSection {...props} />
      : activeSection === 'availability' && props.availability ? <AvailabilitySection key={JSON.stringify(props.availability)} {...props} availability={props.availability} saving={props.isSaving} onSave={props.onSaveAvailability} />
        : activeSection === 'intake' && props.clinicSettings && props.intakeForm ? <IntakeConfigurationSection key={JSON.stringify([props.clinicSettings.intakeForm, props.intakeForm])} settings={props.clinicSettings} form={props.intakeForm} saving={props.isSaving} onSave={props.onSaveClinicSettings} />
          : activeSection === 'reminders' && props.clinicSettings ? <ReminderSection key={JSON.stringify(props.clinicSettings)} settings={props.clinicSettings} saving={props.isSaving} onSave={props.onSaveClinicSettings} />
            : activeSection === 'security' && props.clinicSettings ? <SecuritySection key={JSON.stringify(props.clinicSettings)} settings={props.clinicSettings} saving={props.isSaving} onSave={props.onSaveClinicSettings} />
              : null;

  // Mobile: a card list of sections that drills into one section at a time.
  if (isMobile && activeSection === null) {
    return <div>
      <BackButton label={t('common:actions.back')} onClick={() => navigate('/mobile/more')} />
      <PageHeader title={t('clinicTitle')} subtitle={props.profile?.name ?? t('clinicSubtitle')} />
      <Card className="overflow-hidden">{props.sections.map((item) => <button key={item} type="button" onClick={() => setSection(item)} className="flex w-full items-center gap-3 border-b border-[var(--border)] px-4 py-3 text-left last:border-0">
        <span className="flex h-10 w-10 items-center justify-center rounded-[11px] bg-[var(--surface-2)] text-[var(--text-2)] [&>svg]:h-[18px] [&>svg]:w-[18px]">{icons[item]}</span>
        <span className="flex-1 text-[14px] font-semibold">{labels[item]}</span><ChevronRight className="h-4 w-4 text-[var(--text-3)]" />
      </button>)}</Card></div>;
  }

  if (isMobile) {
    return <div>
      <BackButton label={t('clinicTitle')} onClick={() => setSection(null)} />
      {activeSection && <h1 className="mb-4 text-[22px] font-bold">{labels[activeSection]}</h1>}
      {body}
    </div>;
  }

  // Desktop: a grouped left sub-nav + a content pane with a section header, mirroring the design's
  // settings layout. Only sections the role can reach appear in the rail.
  const visibleGroups = sectionGroups
    .map((group) => ({ groupKey: group.groupKey, sections: group.sections.filter((item) => props.sections.includes(item)) }))
    .filter((group) => group.sections.length > 0);

  return <div className="pb-12">
    <PageHeader title={t('clinicTitle')} subtitle={props.profile?.name ?? t('clinicSubtitle')} />
    <div className="grid items-start gap-7 lg:grid-cols-[minmax(0,220px)_minmax(0,1fr)]">
      <nav aria-label={t('clinicTitle')} className="scroll-hide flex gap-5 overflow-x-auto pb-1 max-lg:border-b max-lg:border-[var(--border)] lg:sticky lg:top-4 lg:flex-col lg:gap-4 lg:overflow-visible lg:pb-0">
        {visibleGroups.map((group) => (
          <div key={group.groupKey} className="shrink-0">
            <div className="px-2.5 pb-1.5 text-[10.5px] font-bold uppercase tracking-[0.06em] text-[var(--text-3)]">{t(group.groupKey)}</div>
            <div className="flex gap-1 lg:flex-col lg:gap-0.5">
              {group.sections.map((item) => {
                const on = activeSection === item;
                return (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setSection(item)}
                    className={cn(
                      'flex items-center gap-2.5 whitespace-nowrap rounded-[9px] px-2.5 py-2 text-left text-[13.5px] font-semibold transition-colors [&>span>svg]:h-4 [&>span>svg]:w-4',
                      on ? 'bg-[var(--primary-weak)] text-[var(--primary)]' : 'text-[var(--text-2)] hover:bg-[var(--surface-2)]'
                    )}
                  >
                    <span className="flex items-center" aria-hidden="true">{icons[item]}</span>
                    <span className="flex-1">{labels[item]}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
      <div key={activeSection ?? 'none'} className="flex flex-col gap-4">
        {activeSection && (
          <div className="border-b border-[var(--border)] pb-3.5">
            <div className="text-[18px] font-bold text-[var(--text)]">{labels[activeSection]}</div>
            <div className="mt-0.5 text-[13px] text-[var(--text-2)]">{descriptions[activeSection]}</div>
          </div>
        )}
        {body}
      </div>
    </div>
  </div>;
}

function ProfileSection({ profile, saving, onSave }: { profile: ClinicProfile; saving: boolean; onSave: Props['onSaveProfile'] }) {
  const { t } = useTranslation('settings');
  const [name, setName] = useState(profile.name);
  const [registrationNumber, setRegistrationNumber] = useState(profile.registrationNumber ?? '');
  const initialPhone = splitE164(profile.phone, profile.address?.countryCode || 'US');
  const [phoneCountry, setPhoneCountry] = useState(initialPhone.country);
  const [phone, setPhone] = useState(initialPhone.national);
  const [email, setEmail] = useState(profile.email ?? '');
  const [location, setLocation] = useState<LocationValue>({
    country: profile.address?.countryCode ?? '',
    stateCode: profile.address?.stateCode ?? '',
    city: profile.address?.city ?? '',
  });
  const [street, setStreet] = useState(profile.address?.street ?? '');
  const [postalCode, setPostalCode] = useState(profile.address?.postalCode ?? '');
  const [timezone, setTimezone] = useState(profile.timezone);
  const [currency, setCurrency] = useState(profile.currency);

  const submit = async () => {
    const dial = dialCodeFor(phoneCountry) ?? '';
    const e164 = phone.trim() ? toE164(dial, phone) : '';
    const form: UpdateClinicProfileRequest = {
      name, timezone, currency, logoUrl: profile.logoUrl,
      phone: e164 || null, email: email || null, registrationNumber: registrationNumber || null,
      street: street || null, city: location.city || null, stateCode: location.stateCode || null,
      country: location.country || null, postalCode: postalCode || null,
    };
    try {
      await onSave(form);
      showToast({ type: 'success', message: t('toasts.profileSaved') });
    } catch (error) {
      toastApiError(error, t('toasts.actionFailed'));
    }
  };

  return <form onSubmit={(event) => { event.preventDefault(); void submit(); }} className="flex flex-col gap-4">
    <Card>
      <CardHeader bordered><CardTitle>{t('profile.detailsSection')}</CardTitle></CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <Input label={t('profile.clinicName')} placeholder={t('profile.namePlaceholder')} value={name} onChange={(e) => setName(e.target.value)} required />
        <Input label={t('profile.registrationNo')} optional placeholder={t('profile.registrationNoPlaceholder')} value={registrationNumber} onChange={(e) => setRegistrationNumber(e.target.value)} />
      </CardContent>
    </Card>

    <Card>
      <CardHeader bordered><CardTitle>{t('profile.contactSection')}</CardTitle><p className="mt-0.5 text-[12.5px] text-[var(--text-3)]">{t('profile.contactHint')}</p></CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <PhoneInput
          label={t('profile.phone')}
          optional
          placeholder={t('profile.phonePlaceholder')}
          country={phoneCountry}
          onCountryChange={setPhoneCountry}
          value={phone}
          onChange={setPhone}
          countryButtonLabel={t('profile.countryButtonLabel')}
          searchPlaceholder={t('profile.searchCountry')}
          noResultsLabel={t('profile.noCountries')}
          maxLength={nationalPhoneLengthLimit(phoneCountry)}
        />
        <Input label={t('profile.email')} optional type="email" placeholder={t('profile.emailPlaceholder')} value={email} onChange={(e) => setEmail(e.target.value)} />
      </CardContent>
    </Card>

    <Card>
      <CardHeader bordered><CardTitle>{t('profile.addressSection')}</CardTitle></CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <LocationSelect
          value={location}
          onChange={setLocation}
          labels={{ country: t('profile.country'), state: t('profile.state'), city: t('profile.city') }}
          placeholders={{ country: t('profile.countryPlaceholder'), state: t('profile.statePlaceholder'), city: t('profile.cityPlaceholder') }}
          searchPlaceholders={{ country: t('profile.searchCountry'), state: t('profile.searchState'), city: t('profile.searchCity') }}
          noResultsLabels={{ country: t('profile.noCountries'), state: t('profile.noStates'), city: t('profile.noCities') }}
          trailing={<Input label={t('profile.postalCode')} optional placeholder={t('profile.postalCodePlaceholder')} value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />}
        />
        <Input label={t('profile.street')} optional placeholder={t('profile.streetPlaceholder')} value={street} onChange={(e) => setStreet(e.target.value)} className="sm:col-span-2" />
      </CardContent>
    </Card>

    <Card>
      <CardHeader bordered><CardTitle>{t('profile.preferencesSection')}</CardTitle></CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <SearchSelect
          label={t('profile.timezone')}
          appearance="input"
          requiredMark
          value={timezone}
          onChange={setTimezone}
          placeholder={t('profile.selectPlaceholder')}
          options={withValue(TIMEZONES, timezone).map((tz) => ({ value: tz, label: tz }))}
          searchPlaceholder={t('profile.searchCountry')}
          noResultsLabel={t('profile.noCountries')}
        />
        <SearchSelect
          label={t('profile.currency')}
          appearance="input"
          requiredMark
          value={currency}
          onChange={setCurrency}
          placeholder={t('profile.selectPlaceholder')}
          options={withValue(CURRENCIES, currency).map((code) => ({ value: code, label: code }))}
          searchPlaceholder={t('profile.searchCountry')}
          noResultsLabel={t('profile.noCountries')}
        />
      </CardContent>
    </Card>

    <div><Button variant="primary" type="submit" isLoading={saving}>{t('profile.save')}</Button></div>
  </form>;
}

function ServicesSection(props: Props) {
  const { t } = useTranslation('settings');
  const [service, setService] = useState({ name: '', defaultPrice: 0, defaultDurationMinutes: 15 as number | null });
  const [roomName, setRoomName] = useState('');
  const addService = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await props.onAddService(service);
      showToast({ type: 'success', message: t('toasts.serviceAdded') });
      setService({ name: '', defaultPrice: 0, defaultDurationMinutes: 15 });
    } catch (error) {
      toastApiError(error, t('toasts.actionFailed'));
    }
  };
  const addRoom = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await props.onAddRoom(roomName);
      showToast({ type: 'success', message: t('toasts.roomAdded') });
      setRoomName('');
    } catch (error) {
      toastApiError(error, t('toasts.actionFailed'));
    }
  };
  return <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
    {props.canManageServices && <Card><CardHeader bordered><CardTitle>{t('services.title')}</CardTitle></CardHeader><CardContent className="space-y-3">
      <form className="grid gap-2 sm:grid-cols-[1fr_120px_110px_auto]" onSubmit={addService}>
        <Input aria-label={t('services.name')} placeholder={t('services.name')} value={service.name} onChange={(event) => setService((value) => ({ ...value, name: event.target.value }))} required />
        <Input aria-label={t('services.price')} type="number" min={0} value={service.defaultPrice} onChange={(event) => setService((value) => ({ ...value, defaultPrice: Number(event.target.value) }))} />
        <Input aria-label={t('services.duration')} type="number" min={1} value={service.defaultDurationMinutes ?? ''} onChange={(event) => setService((value) => ({ ...value, defaultDurationMinutes: Number(event.target.value) }))} />
        <Button type="submit" variant="ghost">{t('actions.add')}</Button>
      </form>
      {props.services.map((item) => <ServiceSettingsRow key={`${item.id}-${item.name}-${item.defaultPrice}-${item.defaultDurationMinutes}-${item.isActive}`} item={item} onSave={props.onUpdateService} />)}
    </CardContent></Card>}
    {props.canManageRooms && <Card><CardHeader bordered><CardTitle>{t('services.rooms')}</CardTitle></CardHeader><CardContent className="space-y-3"><form className="flex gap-2" onSubmit={addRoom}><Input aria-label={t('services.roomName')} value={roomName} onChange={(event) => setRoomName(event.target.value)} placeholder={t('services.roomName')} required /><Button type="submit" variant="ghost">{t('actions.add')}</Button></form>{props.rooms.map((room) => <RoomSettingsRow key={`${room.id}-${room.name}-${room.isActive}`} item={room} onSave={props.onUpdateRoom} />)}</CardContent></Card>}
  </div>;
}

function ServiceSettingsRow({ item, onSave }: { item: ServiceItem; onSave: Props['onUpdateService'] }) {
  const { t } = useTranslation('settings');
  const [value, setValue] = useState(item);
  const [deactivateConfirming, setDeactivateConfirming] = useState(false);
  const save = async () => {
    try {
      await onSave(value);
      showToast({ type: 'success', message: t('toasts.serviceSaved') });
    } catch (error) {
      toastApiError(error, t('toasts.actionFailed'));
    }
  };
  const setActive = async (isActive: boolean) => {
    try {
      await onSave({ ...value, isActive });
      showToast({ type: 'success', message: t(isActive ? 'toasts.serviceActivated' : 'toasts.serviceDeactivated', { name: value.name }) });
    } catch (error) {
      toastApiError(error, t('toasts.actionFailed'));
    }
  };
  return <div className="grid items-center gap-2 rounded-[10px] border border-[var(--border)] p-2 sm:grid-cols-[1fr_100px_90px_auto_auto]">
    <Input aria-label={`${t('services.name')} ${item.name}`} value={value.name} onChange={(event) => setValue({ ...value, name: event.target.value })} />
    <Input aria-label={`${t('services.price')} ${item.name}`} type="number" min={0} value={value.defaultPrice} onChange={(event) => setValue({ ...value, defaultPrice: Number(event.target.value) })} />
    <Input aria-label={`${t('services.duration')} ${item.name}`} type="number" min={1} value={value.defaultDurationMinutes ?? ''} onChange={(event) => setValue({ ...value, defaultDurationMinutes: Number(event.target.value) })} />
    <Button variant="ghost-sm" onClick={() => void save()}>{t('actions.save')}</Button>
    <Button variant="ghost-sm" onClick={() => value.isActive ? setDeactivateConfirming(true) : void setActive(true)}>{value.isActive ? t('actions.deactivate') : t('actions.activate')}</Button>
    {deactivateConfirming && (
      <ConfirmDialog
        title={t('confirm.deactivateServiceTitle', { name: value.name })}
        description={t('confirm.deactivateServiceDescription')}
        confirmLabel={t('actions.deactivate')}
        cancelLabel={t('confirm.keep')}
        onCancel={() => setDeactivateConfirming(false)}
        onConfirm={() => { setDeactivateConfirming(false); void setActive(false); }}
      />
    )}
  </div>;
}

function RoomSettingsRow({ item, onSave }: { item: RoomItem; onSave: Props['onUpdateRoom'] }) {
  const { t } = useTranslation('settings');
  const [value, setValue] = useState(item);
  const [deactivateConfirming, setDeactivateConfirming] = useState(false);
  const save = async () => {
    try {
      await onSave(value);
      showToast({ type: 'success', message: t('toasts.roomSaved') });
    } catch (error) {
      toastApiError(error, t('toasts.actionFailed'));
    }
  };
  const setActive = async (isActive: boolean) => {
    try {
      await onSave({ ...value, isActive });
      showToast({ type: 'success', message: t(isActive ? 'toasts.roomActivated' : 'toasts.roomDeactivated', { name: value.name }) });
    } catch (error) {
      toastApiError(error, t('toasts.actionFailed'));
    }
  };
  return <div className="flex items-center gap-2 border-b border-[var(--border)] py-2 last:border-0">
    <Input aria-label={`${t('services.roomName')} ${item.name}`} value={value.name} onChange={(event) => setValue({ ...value, name: event.target.value })} />
    <Button variant="ghost-sm" onClick={() => void save()}>{t('actions.save')}</Button>
    <Button variant="ghost-sm" onClick={() => value.isActive ? setDeactivateConfirming(true) : void setActive(true)}>{value.isActive ? t('actions.deactivate') : t('actions.activate')}</Button>
    {deactivateConfirming && (
      <ConfirmDialog
        title={t('confirm.deactivateRoomTitle', { name: value.name })}
        description={t('confirm.deactivateRoomDescription')}
        confirmLabel={t('actions.deactivate')}
        cancelLabel={t('confirm.keep')}
        onCancel={() => setDeactivateConfirming(false)}
        onConfirm={() => { setDeactivateConfirming(false); void setActive(false); }}
      />
    )}
  </div>;
}

/* Legacy single-interval availability view.
function AvailabilitySection({ availability, saving, onSave }: { availability: Availability; saving: boolean; onSave: Props['onSaveAvailability'] }) {
  const { t } = useTranslation('settings');
  const [value, setValue] = useState(availability);
  const updateTime = (dayIndex: number, key: 'start' | 'end', next: string) => setValue((current) => ({ ...current, clinicDays: current.clinicDays.map((day, index) => index === dayIndex ? { ...day, intervals: [{ start: day.intervals[0]?.start ?? '09:00', end: day.intervals[0]?.end ?? '17:00', [key]: next }] } : day) }));
  return <div className="space-y-4"><Card><CardHeader bordered><CardTitle>{t('availability.weeklyHours')}</CardTitle></CardHeader><CardContent className="space-y-2">{value.clinicDays.map((day, index) => <div key={day.day} className="grid grid-cols-[90px_1fr_1fr] items-center gap-2"><span className="text-[13px] font-semibold">{t(`days.${day.day.toLowerCase()}`)}</span><Input aria-label={`${day.day} ${t('availability.start')}`} type="time" value={day.intervals[0]?.start ?? ''} onChange={(event) => updateTime(index, 'start', event.target.value)} /><Input aria-label={`${day.day} ${t('availability.end')}`} type="time" value={day.intervals[0]?.end ?? ''} onChange={(event) => updateTime(index, 'end', event.target.value)} /></div>)}</CardContent></Card>
    <div className="grid gap-4 lg:grid-cols-2"><Card><CardHeader bordered><CardTitle>{t('availability.providerHours')}</CardTitle></CardHeader><CardContent>{value.providers.map((provider) => <div key={provider.providerUserId} className="border-b border-[var(--border)] py-2"><div className="font-semibold">{provider.providerName ?? provider.providerUserId}</div><div className="text-[12px] text-[var(--text-3)]">{provider.days.filter((day) => day.intervals.length).length} {t('availability.workingDays')}</div></div>)}</CardContent></Card><Card><CardHeader bordered><CardTitle>{t('availability.holidays')}</CardTitle></CardHeader><CardContent>{value.holidays.map((holiday) => <div key={holiday.id} className="flex gap-3 border-b border-[var(--border)] py-2"><span className="font-semibold text-[var(--primary)]">{holiday.date}</span><span>{holiday.name}</span></div>)}</CardContent></Card></div>
    <Card><CardHeader bordered><CardTitle>{t('availability.leaveRequests')}</CardTitle></CardHeader><CardContent>{value.leaves.map((leave) => <div key={leave.id} className="flex items-center gap-3 border-b border-[var(--border)] py-2"><div className="flex-1"><div className="font-semibold">{leave.providerName ?? leave.providerUserId}</div><div className="text-[12px] text-[var(--text-3)]">{leave.startDate} – {leave.endDate} · {leave.reason}</div></div><Badge variant={leave.status === 'Approved' ? 'success' : 'warning'}>{leave.status}</Badge></div>)}</CardContent></Card>
    <Button variant="primary" isLoading={saving} onClick={() => void onSave({ clinicDays: value.clinicDays, providers: value.providers, holidays: value.holidays })}>{t('availability.save')}</Button></div>;
}

*/

function AvailabilitySection({ availability, saving, onSave, canManageAvailability, onCreateLeave, onDecideLeave, onCancelLeave, clinicSettings, onSaveClinicSettings, isSaving }: Props & { availability: Availability; saving: boolean; onSave: Props['onSaveAvailability'] }) {
  const { t } = useTranslation('settings');
  const { timeFormat } = useTimeFormat();
  const [value, setValue] = useState(availability);
  const [leave, setLeave] = useState({ startDate: '', endDate: '', startTime: '', endTime: '', reason: '' });
  const [smartQueue, setSmartQueue] = useState(clinicSettings?.smartQueueEnabled ?? false);
  const [maxPerBracket, setMaxPerBracket] = useState(clinicSettings?.maxAppointmentsPerBracket ?? 0);
  const [allowFreeFormScheduling, setAllowFreeFormScheduling] = useState(
    clinicSettings?.allowFreeFormScheduling ?? false,
  );
  const saveQueuePolicy = async () => {
    if (!clinicSettings) return;
    try {
      await onSaveClinicSettings({
        ...clinicSettings,
        smartQueueEnabled: smartQueue,
        maxAppointmentsPerBracket: maxPerBracket,
        allowFreeFormScheduling,
      });
      showToast({ type: 'success', message: t('toasts.settingsSaved') });
    } catch (error) {
      toastApiError(error, t('toasts.actionFailed'));
    }
  };
  const updateTime = (dayIndex: number, intervalIndex: number, key: 'start' | 'end', next: string) => setValue((current) => ({ ...current, clinicDays: current.clinicDays.map((day, index) => index === dayIndex ? { ...day, intervals: day.intervals.map((interval, itemIndex) => itemIndex === intervalIndex ? { ...interval, [key]: next } : interval) } : day) }));
  const addInterval = (dayIndex: number) => setValue((current) => ({ ...current, clinicDays: current.clinicDays.map((day, index) => index === dayIndex ? { ...day, intervals: [...day.intervals, { start: '09:00', end: '17:00' }] } : day) }));
  const removeInterval = (dayIndex: number, intervalIndex: number) => setValue((current) => ({ ...current, clinicDays: current.clinicDays.map((day, index) => index === dayIndex ? { ...day, intervals: day.intervals.filter((_, itemIndex) => itemIndex !== intervalIndex) } : day) }));
  const [cancelLeaveTarget, setCancelLeaveTarget] = useState<string | null>(null);

  const saveAvailability = async () => {
    try {
      await onSave({ clinicDays: value.clinicDays, providers: value.providers, holidays: value.holidays });
      showToast({ type: 'success', message: t('toasts.availabilitySaved') });
    } catch (error) {
      toastApiError(error, t('toasts.actionFailed'));
    }
  };
  const submitLeave = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await onCreateLeave({ startDate: leave.startDate, endDate: leave.endDate, startTime: leave.startTime || null, endTime: leave.endTime || null, reason: leave.reason });
      showToast({ type: 'success', message: t('toasts.leaveRequested') });
      setLeave({ startDate: '', endDate: '', startTime: '', endTime: '', reason: '' });
    } catch (error) {
      toastApiError(error, t('toasts.actionFailed'));
    }
  };
  const decideLeave = async (leaveId: string, decision: 'Approved' | 'Rejected') => {
    try {
      await onDecideLeave({ leaveId, decision });
      showToast({ type: 'success', message: t('toasts.leaveDecided') });
    } catch (error) {
      toastApiError(error, t('toasts.actionFailed'));
    }
  };
  const cancelLeave = async (leaveId: string) => {
    try {
      await onCancelLeave(leaveId);
      showToast({ type: 'success', message: t('toasts.leaveCancelled') });
    } catch (error) {
      toastApiError(error, t('toasts.actionFailed'));
    } finally {
      setCancelLeaveTarget(null);
    }
  };
  return <div className="space-y-4">
    {canManageAvailability && <Card><CardHeader bordered><CardTitle>{t('availability.weeklyHours')}</CardTitle></CardHeader><CardContent className="space-y-3">{value.clinicDays.map((day, dayIndex) => <div key={day.day} className="grid gap-2 border-b border-[var(--border)] pb-3 last:border-0 sm:grid-cols-[100px_1fr]"><span className="pt-3 text-[13px] font-semibold">{t(`days.${day.day.toLowerCase()}`)}</span><div className="space-y-2">{day.intervals.map((interval, intervalIndex) => <div key={`${day.day}-${intervalIndex}`} className="grid grid-cols-[1fr_1fr_auto] gap-2"><TimeField value={interval.start} timeFormat={timeFormat} minuteStep={5} triggerAriaLabel={`${day.day} ${t('availability.start')}`} onChange={(next) => updateTime(dayIndex, intervalIndex, 'start', next)} /><TimeField value={interval.end} timeFormat={timeFormat} minuteStep={5} triggerAriaLabel={`${day.day} ${t('availability.end')}`} onChange={(next) => updateTime(dayIndex, intervalIndex, 'end', next)} /><Button variant="ghost-sm" onClick={() => removeInterval(dayIndex, intervalIndex)}>{t('actions.remove')}</Button></div>)}<Button variant="ghost-sm" onClick={() => addInterval(dayIndex)}>{day.intervals.length ? t('availability.addInterval') : t('availability.markOpen')}</Button></div></div>)}</CardContent></Card>}
    {canManageAvailability && <div className="grid gap-4 lg:grid-cols-2"><Card><CardHeader bordered><CardTitle>{t('availability.providerHours')}</CardTitle></CardHeader><CardContent>{value.providers.map((provider) => <div key={provider.providerUserId} className="border-b border-[var(--border)] py-2"><div className="font-semibold">{provider.providerName ?? provider.providerUserId}</div><div className="text-[12px] text-[var(--text-3)]">{provider.days.filter((day) => day.intervals.length).length} {t('availability.workingDays')}</div></div>)}</CardContent></Card><Card><CardHeader bordered><CardTitle>{t('availability.holidays')}</CardTitle></CardHeader><CardContent>{value.holidays.map((holiday) => <div key={holiday.id} className="flex items-center gap-3 border-b border-[var(--border)] py-2"><span className="font-semibold text-[var(--primary)]">{holiday.date}</span><span className="flex-1">{holiday.name}</span><Button variant="ghost-sm" onClick={() => setValue((current) => ({ ...current, holidays: current.holidays.map((entry) => entry.id === holiday.id ? { ...entry, isActive: !entry.isActive } : entry) }))}>{holiday.isActive ? t('actions.deactivate') : t('actions.activate')}</Button></div>)}</CardContent></Card></div>}
    {clinicSettings && <Card><CardHeader bordered><CardTitle>{t('security.queuePolicy')}</CardTitle><p className="mt-0.5 text-[12.5px] text-[var(--text-3)]">{t('security.queuePolicyDesc')}</p></CardHeader><CardContent className="space-y-4"><div className="flex items-center justify-between gap-3 rounded-[10px] border border-[var(--border)] p-3"><span><span className="block font-semibold">{t('security.smartQueue')}</span><span className="text-[12px] text-[var(--text-3)]">{t('security.smartQueueHint')}</span></span><Switch checked={smartQueue} onChange={setSmartQueue} label={t('security.smartQueue')} /></div><div className="flex items-center justify-between gap-3 rounded-[10px] border border-[var(--border)] p-3"><span><span className="block font-semibold">{t('security.allowFreeFormScheduling')}</span><span className="text-[12px] text-[var(--text-3)]">{t('security.allowFreeFormSchedulingHint')}</span></span><Switch checked={allowFreeFormScheduling} onChange={setAllowFreeFormScheduling} label={t('security.allowFreeFormScheduling')} /></div><Input className="max-w-[420px]" label={t('security.maxAppointmentsPerBracket')} hint={t('security.maxAppointmentsPerBracketHint')} type="number" min={0} max={100} value={maxPerBracket} onChange={(event) => setMaxPerBracket(Number(event.target.value))} /><Button variant="primary" isLoading={isSaving} onClick={() => void saveQueuePolicy()}>{t('security.saveQueuePolicy')}</Button></CardContent></Card>}
    <Card><CardHeader bordered><CardTitle>{t('availability.requestLeave')}</CardTitle></CardHeader><CardContent><form className="grid gap-3 md:grid-cols-2" onSubmit={submitLeave}><Input label={t('availability.startDate')} type="date" value={leave.startDate} onChange={(event) => setLeave((current) => ({ ...current, startDate: event.target.value }))} required /><Input label={t('availability.endDate')} type="date" value={leave.endDate} onChange={(event) => setLeave((current) => ({ ...current, endDate: event.target.value }))} required /><TimeField label={t('availability.startTimeOptional')} optional value={leave.startTime || null} timeFormat={timeFormat} minuteStep={5} onChange={(next) => setLeave((current) => ({ ...current, startTime: next }))} /><TimeField label={t('availability.endTimeOptional')} optional value={leave.endTime || null} timeFormat={timeFormat} minuteStep={5} onChange={(next) => setLeave((current) => ({ ...current, endTime: next }))} /><Input label={t('availability.reason')} value={leave.reason} onChange={(event) => setLeave((current) => ({ ...current, reason: event.target.value }))} required /><div className="flex items-end"><Button variant="ghost" type="submit">{t('availability.submitLeave')}</Button></div></form></CardContent></Card>
    <Card><CardHeader bordered><CardTitle>{t('availability.leaveRequests')}</CardTitle></CardHeader><CardContent>{value.leaves.map((item) => <div key={item.id} className="flex flex-wrap items-center gap-3 border-b border-[var(--border)] py-2"><div className="min-w-[220px] flex-1"><div className="font-semibold">{item.providerName ?? item.providerUserId}</div><div className="text-[12px] text-[var(--text-3)]">{item.startDate} – {item.endDate} · {item.reason}</div></div><Badge variant={item.status === 'Approved' ? 'success' : 'warning'}>{item.status}</Badge>{canManageAvailability && item.status === 'Pending' && <><Button variant="ghost-sm" onClick={() => void decideLeave(item.id, 'Approved')}>{t('availability.approve')}</Button><Button variant="ghost-sm" onClick={() => void decideLeave(item.id, 'Rejected')}>{t('availability.reject')}</Button></>}<Button variant="ghost-sm" onClick={() => setCancelLeaveTarget(item.id)}>{t('availability.cancel')}</Button></div>)}</CardContent></Card>
    {canManageAvailability && <Button variant="primary" isLoading={saving} onClick={() => void saveAvailability()}>{t('availability.save')}</Button>}
    {cancelLeaveTarget && (
      <ConfirmDialog
        title={t('confirm.cancelLeaveTitle')}
        description={t('confirm.cancelLeaveDescription')}
        confirmLabel={t('confirm.confirm')}
        cancelLabel={t('confirm.keep')}
        onCancel={() => setCancelLeaveTarget(null)}
        onConfirm={() => void cancelLeave(cancelLeaveTarget)}
      />
    )}
  </div>;
}

const allIntakeFields: IntakeFieldKey[] = ['Weight', 'BloodPressure', 'Temperature', 'Pulse'];

function IntakeConfigurationSection({ settings, form, saving, onSave }: {
  settings: ClinicSettings;
  form: IntakeFormResponse;
  saving: boolean;
  onSave: Props['onSaveClinicSettings'];
}) {
  const { t } = useTranslation('settings');
  const visiblePreset = form.fields.map((field) => field.key);
  const [order, setOrder] = useState<IntakeFieldKey[]>([
    ...visiblePreset,
    ...allIntakeFields.filter((field) => !visiblePreset.includes(field)),
  ]);
  const [visible, setVisible] = useState<Set<IntakeFieldKey>>(new Set(visiblePreset));
  const [required, setRequired] = useState<Set<IntakeFieldKey>>(
    new Set(form.fields.filter((field) => field.required).map((field) => field.key)),
  );
  const [weightUnit, setWeightUnit] = useState<'kg' | 'lb'>(form.weightUnit);
  const [temperatureUnit, setTemperatureUnit] = useState<'C' | 'F'>(form.temperatureUnit);

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= order.length) return;
    setOrder((current) => {
      const next = [...current];
      [next[index], next[target]] = [next[target]!, next[index]!];
      return next;
    });
  };

  const toggleVisible = (field: IntakeFieldKey, checked: boolean) => {
    setVisible((current) => {
      const next = new Set(current);
      if (checked) next.add(field); else next.delete(field);
      return next;
    });
    if (!checked) {
      setRequired((current) => {
        const next = new Set(current);
        next.delete(field);
        return next;
      });
    }
  };

  const save = async (reset = false) => {
    const intakeForm = reset
      ? { fieldOrder: [], hiddenFields: [], requiredFields: [], weightUnit: null, temperatureUnit: null }
      : {
          fieldOrder: order,
          hiddenFields: allIntakeFields.filter((field) => !visible.has(field)),
          requiredFields: allIntakeFields.filter((field) => required.has(field)),
          weightUnit,
          temperatureUnit,
        };
    try {
      await onSave({ ...settings, intakeForm });
      showToast({ type: 'success', message: t(reset ? 'intake.resetSaved' : 'intake.saved') });
    } catch (error) {
      toastApiError(error, t('toasts.actionFailed'));
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-[12px] bg-[var(--info-bg)] p-4">
        <p className="text-[13px] font-semibold text-[var(--info)]">{t('intake.preset', { type: form.clinicType })}</p>
        <p className="mt-1 max-w-[70ch] text-[12.5px] leading-relaxed text-[var(--text-2)]">{t('intake.description')}</p>
      </div>
      <Card>
        <CardHeader bordered><CardTitle>{t('intake.fieldsTitle')}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {order.map((field, index) => (
            <div key={field} className="grid items-center gap-3 rounded-[10px] bg-[var(--surface-2)] p-3 sm:grid-cols-[1fr_auto_auto]">
              <div>
                <p className="text-[13.5px] font-semibold">{t(`intake.fields.${field}`)}</p>
                <p className="text-[12px] text-[var(--text-3)]">{t('intake.typedField')}</p>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={visible.has(field)} onChange={(checked) => toggleVisible(field, checked)} label={t('intake.visible')} />
                <span className="text-[12px] text-[var(--text-2)]">{t('intake.visible')}</span>
                <Switch checked={required.has(field)} disabled={!visible.has(field)} onChange={(checked) => setRequired((current) => {
                  const next = new Set(current);
                  if (checked) next.add(field); else next.delete(field);
                  return next;
                })} label={t('intake.required')} />
                <span className="text-[12px] text-[var(--text-2)]">{t('intake.required')}</span>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost-sm" aria-label={t('intake.moveUp', { field: t(`intake.fields.${field}`) })} disabled={index === 0} onClick={() => move(index, -1)}><ChevronUp className="h-4 w-4" /></Button>
                <Button variant="ghost-sm" aria-label={t('intake.moveDown', { field: t(`intake.fields.${field}`) })} disabled={index === order.length - 1} onClick={() => move(index, 1)}><ChevronDown className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
      <Card>
        <CardHeader bordered><CardTitle>{t('intake.unitsTitle')}</CardTitle></CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <SearchSelect
            label={t('intake.weightUnit')}
            searchable={false}
            value={weightUnit}
            onChange={(value) => setWeightUnit(value as 'kg' | 'lb')}
            options={[
              { value: 'kg', label: t('intake.units.kg') },
              { value: 'lb', label: t('intake.units.lb') },
            ]}
          />
          <SearchSelect
            label={t('intake.temperatureUnit')}
            searchable={false}
            value={temperatureUnit}
            onChange={(value) => setTemperatureUnit(value as 'C' | 'F')}
            options={[
              { value: 'C', label: t('intake.units.C') },
              { value: 'F', label: t('intake.units.F') },
            ]}
          />
        </CardContent>
      </Card>
      <div className="flex flex-wrap gap-2">
        <Button variant="primary" isLoading={saving} onClick={() => void save()}>{t('intake.save')}</Button>
        <Button variant="ghost" disabled={saving} onClick={() => void save(true)}>{t('intake.reset')}</Button>
      </div>
    </div>
  );
}

function ReminderSection({ settings, saving, onSave }: { settings: ClinicSettings; saving: boolean; onSave: Props['onSaveClinicSettings'] }) {
  const { t } = useTranslation('settings');
  const [value, setValue] = useState(settings);
  const toggle = (key: keyof ClinicSettings, label: string) => (
    <div className="flex items-center justify-between gap-3 rounded-[10px] border border-[var(--border)] p-3">
      <span className="font-semibold">{label}</span>
      <Switch
        checked={Boolean(value[key])}
        onChange={(checked) => setValue((current) => ({ ...current, [key]: checked }))}
        label={label}
      />
    </div>
  );
  const saveReminders = async () => {
    try {
      await onSave(value);
      showToast({ type: 'success', message: t('toasts.settingsSaved') });
    } catch (error) {
      toastApiError(error, t('toasts.actionFailed'));
    }
  };
  return <Card><CardContent className="space-y-4">
    <div className="grid gap-3 lg:grid-cols-3">{toggle('smsRemindersEnabled', t('reminders.sms'))}{toggle('whatsAppRemindersEnabled', t('reminders.whatsapp'))}{toggle('emailRemindersEnabled', t('reminders.email'))}</div>
    <Input className="max-w-[420px]" label={t('reminders.offsets')} value={value.reminderOffsetsMinutes.join(', ')} onChange={(event) => setValue((current) => ({ ...current, reminderOffsetsMinutes: event.target.value.split(',').map((item) => Number(item.trim())).filter((item) => item > 0) }))} hint={t('reminders.offsetHint')} />
    <p className="rounded-[10px] bg-[var(--info-bg)] p-3 text-[13px] text-[var(--info)]">{t('reminders.systemManaged')}</p>
    <Button variant="primary" isLoading={saving} onClick={() => void saveReminders()}>{t('reminders.save')}</Button>
  </CardContent></Card>;
}

function SecuritySection({ settings, saving, onSave }: { settings: ClinicSettings; saving: boolean; onSave: Props['onSaveClinicSettings'] }) {
  const { t } = useTranslation('settings');
  const [required, setRequired] = useState(settings.requireTwoFactorForStaff);

  const saveSignInPolicy = async () => {
    try {
      await onSave({ ...settings, requireTwoFactorForStaff: required });
      showToast({ type: 'success', message: t('toasts.settingsSaved') });
    } catch (error) {
      toastApiError(error, t('toasts.actionFailed'));
    }
  };

  return <Card>
    <CardHeader bordered><CardTitle>{t('security.signInPolicy')}</CardTitle><p className="mt-0.5 text-[12.5px] text-[var(--text-3)]">{t('security.signInPolicyDesc')}</p></CardHeader>
    <CardContent className="space-y-4">
      <div className="flex items-center justify-between gap-3 rounded-[10px] border border-[var(--border)] p-3">
        <span><span className="block font-semibold">{t('security.requireTwoFactor')}</span><span className="text-[12px] text-[var(--text-3)]">{t('security.requireTwoFactorHint')}</span></span>
        <Switch checked={required} onChange={setRequired} label={t('security.requireTwoFactor')} />
      </div>
      <Button variant="primary" isLoading={saving} onClick={() => void saveSignInPolicy()}>{t('security.saveSignInPolicy')}</Button>
    </CardContent>
  </Card>;
}
